function configs(labels, datasetsInput) {
  const datasetsArray = Array.isArray(datasetsInput) ? datasetsInput : [datasetsInput];
  const formatTooltipDate = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const raw = String(value).split("T")[0];
    const isoParts = raw.split("-");
    const isoMonthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    if (isoParts.length === 3 && isoParts[0].length === 4) {
      return `${isoParts[2]}-${isoMonthNames[Number(isoParts[1]) - 1]}`;
    }
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const iso = raw.match(/^(\\d{4})-(\\d{2})-(\\d{2})$/);
    if (iso) return `${iso[3]}-${months[Number(iso[2]) - 1]}`;
    const dmy = raw.match(/^(\\d{2})-(\\d{2})-(\\d{4})$/);
    if (dmy) return `${dmy[1]}-${months[Number(dmy[2]) - 1]}`;
    return raw;
  };
  const formatDayDateTitle = (label, metadata = {}) => {
    const day = metadata.day ?? (/^-?\\d+(?:\\.\\d+)?$/.test(String(label)) ? label : null);
    if (day === null || day === undefined) return label ?? "";
    const date = formatTooltipDate(metadata.date);
    return `D${day}${date ? ` (${date})` : ""}`;
  };

  const datasets = datasetsArray.map((dataset) => ({
    label: dataset.label,
    data: dataset.data,
    backgroundColor: dataset.backgroundColor,
    tooltipDisplay: dataset.tooltipDisplay,
    tooltipDisplayColors: dataset.tooltipDisplayColors,
    borderRadius: 4,
    borderSkipped: false,
    maxBarThickness: dataset.maxBarThickness ?? 20,
    barPercentage: dataset.barPercentage ?? 0.6, // NEW: control bar width inside group
    categoryPercentage: dataset.categoryPercentage ?? 0.8,
    metadata: dataset.metadata,
    datalabels: dataset.datalabels,
  }));

  const suppressTooltipColors = datasets.some(
    (dataset) =>
      dataset.tooltipDisplayColors === false ||
      dataset.metadata?.some((metadata) => metadata?.cv_status !== undefined)
  );

  return {
    data: {
      labels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 0,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          displayColors: !suppressTooltipColors,
          filter: function (context) {
            return context.dataset?.tooltipDisplay !== false;
          },
          callbacks: {
            title: function (items) {
              const firstItem = Array.isArray(items) ? items[0] : null;
              const metadata = firstItem?.dataset?.metadata?.[firstItem?.dataIndex] || {};
              return formatDayDateTitle(firstItem?.label, metadata);
            },
            label: function (context) {
              const dataset = context.dataset;
              const index = context.dataIndex;
              const value = dataset.data?.[index];
              const metadata = dataset.metadata?.[index] || {};
              const label = dataset.label?.toLowerCase();

              if (value === null || value === undefined) return "";

              const lines = [];

              const formatMetric = (metric, fractionDigits) => {
                if (metric === null || metric === undefined || metric === "null") return null;
                const numeric = Number(metric);
                if (Number.isFinite(numeric)) {
                  return numeric.toFixed(fractionDigits);
                }
                return String(metric);
              };
              const formatMetricPreserve = (metric) => {
                if (metric === null || metric === undefined || metric === "null") return null;
                return String(metric);
              };

              const hasTopIssuesEnvironmentMetadata =
                metadata.avgVal !== undefined ||
                metadata.targetL !== undefined ||
                metadata.targetH !== undefined ||
                metadata.lowVal !== undefined ||
                metadata.highVal !== undefined;
              if (hasTopIssuesEnvironmentMetadata) {
                const avgValue = formatMetricPreserve(metadata.avgVal);
                const avgDevValue = formatMetricPreserve(metadata.avgDeviationP ?? value);
                const targetL = formatMetricPreserve(metadata.targetL);
                const targetH = formatMetricPreserve(metadata.targetH);
                const lowVal = formatMetricPreserve(metadata.lowVal);
                const highVal = formatMetricPreserve(metadata.highVal);
                if (avgValue !== null) lines.push(`Avg: ${avgValue}`);
                if (avgDevValue !== null) lines.push(`Avg Dev: ${avgDevValue}`);
                if (targetL !== null || targetH !== null) {
                  lines.push(`Target: ${targetL ?? "-"}-${targetH ?? "-"}`);
                }
                if (lowVal !== null || highVal !== null) {
                  lines.push(`Range: ${lowVal ?? "-"}-${highVal ?? "-"}`);
                }
                return lines;
              }

              const hasTopIssuesPerformanceMetadata =
                metadata.weekly_weight !== undefined ||
                metadata.deviationp !== undefined ||
                metadata.mortality_today !== undefined ||
                metadata.mortality_rate !== undefined ||
                metadata.target_green !== undefined ||
                metadata.day !== undefined ||
                metadata.weight_average !== undefined ||
                metadata.weight_target !== undefined ||
                metadata.cv !== undefined ||
                metadata.fcr !== undefined ||
                metadata.fcr_target !== undefined;
              if (hasTopIssuesPerformanceMetadata) {
                const dayValue = formatMetricPreserve(metadata.day);
                const avgWeightValue = formatMetric(metadata.weekly_weight, 3);
                const avgDevValue = formatMetricPreserve(metadata.deviationp ?? value);
                const weightValue = formatMetricPreserve(metadata.weight_average);
                const weightTargetValue = formatMetricPreserve(metadata.weight_target);
                const cvValue = formatMetric(metadata.cv, 2);
                const cvTargetValue = formatMetricPreserve(metadata.cv_target);
                const cvStatus = String(metadata.cv_status || "").toLowerCase();
                const cvStatusMarkers = {
                  green: "🟢",
                  red: "🔴",
                  amber: "🟡",
                  yellow: "🟡",
                  orange: "🟠",
                };
                const cvStatusMarker = cvStatusMarkers[cvStatus];
                const fcrValue = formatMetricPreserve(metadata.fcr);
                const fcrTargetValue = formatMetricPreserve(metadata.fcr_target);
                const mortalityValue = formatMetricPreserve(metadata.mortality_today);
                const rateValue = formatMetricPreserve(metadata.mortality_rate);
                const targetValue = formatMetricPreserve(metadata.target_green);
                if (avgWeightValue !== null) lines.push(`Avg Weight: ${avgWeightValue}`);
                if (weightValue !== null) {
                  lines.push(`Weight: ${weightValue}`);
                  if (avgDevValue !== null) lines.push(`Deviation: ${avgDevValue}%`);
                  if (weightTargetValue !== null) lines.push(`Target: ${weightTargetValue}`);
                  return lines;
                }
                if (avgWeightValue !== null) {
                  if (avgDevValue !== null) lines.push(`Deviation: ${avgDevValue}%`);
                  if (weightTargetValue !== null) lines.push(`Weight Target: ${weightTargetValue}`);
                  if (cvValue !== null) {
                    const cvPrefix = cvStatusMarker ? `${cvStatusMarker} CV: ` : "CV: ";
                    lines.push(`${cvPrefix}${cvValue} (0-${cvTargetValue ?? "-"})`);
                  }
                  return lines;
                }
                if (fcrValue !== null) {
                  lines.push(`FCR: ${fcrValue}`);
                  if (avgDevValue !== null) lines.push(`Deviation: ${avgDevValue}%`);
                  if (fcrTargetValue !== null) lines.push(`Target: ${fcrTargetValue}`);
                  return lines;
                }
                if (mortalityValue !== null) {
                  lines.push(`Mortality: ${mortalityValue}`);
                  if (avgDevValue !== null) lines.push(`Avg Dev: ${avgDevValue}`);
                  if (rateValue !== null) lines.push(`Rate: ${rateValue}`);
                  if (targetValue !== null) lines.push(`Target: ${targetValue}`);
                  return lines;
                }
                if (avgDevValue !== null) lines.push(`Avg Dev: ${avgDevValue}`);
                return lines;
              }

              lines.push(`${dataset.label}: ${value}`);

              const formattedFcr = formatMetric(metadata.fcr, 3);
              if (formattedFcr !== null) {
                lines.push(`FCR: ${formattedFcr}`);
              }

              const formattedTarget = formatMetric(metadata.target, 1);
              if (formattedTarget !== null) {
                lines.push(`Target: ${formattedTarget}`);
              }

              const formattedDeviation = formatMetric(metadata.deviationp, 2);
              if (formattedDeviation !== null) {
                lines.push(`Deviation: ${formattedDeviation}%`);
              }

              // Conditionally show mortality or weight data
              if (label?.includes("mortality") && metadata.mortality_rate !== undefined) {
                lines.push(`Mortality Rate: ${metadata.mortality_rate}%`);
              }

              if (label?.includes("weight") && metadata.weight_target !== undefined) {
                lines.push(`Weight Target: ${metadata.weight_target} kg`);
              }

              return lines;
            },
            labelColor: function (context) {
              const metadata = context.dataset?.metadata?.[context.dataIndex] || {};
              const status = String(metadata.cv_status || "").toLowerCase();
              const statusColors = {
                green: "#4caf50",
                red: "#f44336",
                amber: "#ffb300",
                yellow: "#fdd835",
                orange: "#fb8c00",
              };
              const color = statusColors[status];
              if (!color) {
                const fallbackColor = Array.isArray(context.dataset.backgroundColor)
                  ? context.dataset.backgroundColor[context.dataIndex]
                  : context.dataset.backgroundColor;
                const resolvedColor = fallbackColor || "rgba(0, 0, 0, 0)";
                return {
                  borderColor: resolvedColor,
                  backgroundColor: resolvedColor,
                  borderWidth: 0,
                };
              }
              return {
                borderColor: color,
                backgroundColor: color,
                borderWidth: 0,
              };
            },
          },
        },
        datalabels: {
          display: function (context) {
            const datasetLabelsConfig = context.dataset?.datalabels || {};
            if (datasetLabelsConfig.display !== undefined) {
              return typeof datasetLabelsConfig.display === "function"
                ? datasetLabelsConfig.display(context)
                : datasetLabelsConfig.display;
            }
            return context.dataset.label !== "Weight Target"; // Hide labels for "Weight Target"
          },
          anchor: function (context) {
            const datasetLabelsConfig = context.dataset?.datalabels || {};
            return datasetLabelsConfig.anchor ?? "end";
          },
          align: function (context) {
            const datasetLabelsConfig = context.dataset?.datalabels || {};
            return datasetLabelsConfig.align ?? "top";
          },
          color: function (context) {
            const datasetLabelsConfig = context.dataset?.datalabels || {};
            return datasetLabelsConfig.color ?? "#fff";
          },
          font: {
            weight: "bold",
          },
          rotation: function (context) {
            const datasetLabelsConfig = context.dataset?.datalabels || {};
            return datasetLabelsConfig.rotation ?? 0;
          },
          offset: function (context) {
            const datasetLabelsConfig = context.dataset?.datalabels || {};
            return datasetLabelsConfig.offset ?? 0;
          },
          textAlign: "center",
          textAnchor: "end",
          /*rotation: function (context) {
            const label = context.dataset.label?.toLowerCase();
            return label.includes("weekly weight") || label.includes("weight target") ? -45 : 0;
          },**/
          formatter: function (value, context) {
            const datasetLabelsConfig = context.dataset?.datalabels || {};
            if (typeof datasetLabelsConfig.formatter === "function") {
              return datasetLabelsConfig.formatter(value, context);
            }
            return value === null ? "" : Number(value).toFixed(2);
          },
        },
      },
      interaction: {
        intersect: false,
        mode: "index",
      },
      scales: {
        y: {
          grid: {
            drawBorder: false,
            display: true,
            drawOnChartArea: true,
            drawTicks: false,
            borderDash: [5, 5],
            color: "rgba(255, 255, 255, .2)",
          },
          ticks: {
            beginAtZero: true,
            padding: 10,
            font: {
              size: 14,
              weight: 300,
              family: "Roboto",
              style: "normal",
              lineHeight: 2,
            },
            color: "#fff",
          },
        },
        x: {
          stacked: false,
          grid: {
            drawBorder: false,
            display: true,
            drawOnChartArea: true,
            drawTicks: false,
            borderDash: [5, 5],
            color: "rgba(255, 255, 255, .2)",
          },
          ticks: {
            color: "#f8f9fa",
            padding: 10,
            font: {
              size: 14,
              weight: 300,
              family: "Roboto",
              style: "normal",
              lineHeight: 2,
            },
          },
        },
      },
    },
  };
}

export default configs;
